import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  interval,
  Subject,
  Subscription,
} from 'rxjs';
import { defaultIfEmpty, filter, take, takeUntil } from 'rxjs/operators';
import { ProductTrackingInfo } from '@usf/ngrx-product';
import { ProductDetailService } from './product-detail.service';

@Injectable({
  providedIn: 'root',
})
export class ProductDetailTrackPageLoadService {
  // Observable string sources
  private customerAlsoBoughtSubject = new Subject<ProductTrackingInfo[]>();
  private mayAlsoNeedSubject = new BehaviorSubject<ProductTrackingInfo[]>(
    undefined,
  );
  private ratingStartsSubject = new BehaviorSubject<number>(0);
  private questionCountSubject = new Subject<number>();

  private recommendations$: Subscription;

  // Observable string streams
  customerAlsoBought$ = this.customerAlsoBoughtSubject.asObservable();
  mayAlsoNeed$ = this.mayAlsoNeedSubject.asObservable();
  ratingStarts$ = this.ratingStartsSubject.asObservable();
  questionCount$ = this.questionCountSubject.asObservable();

  constructor(private productDetailService: ProductDetailService) {}

  nextCustomerAlsoBought(productTrackingInfos: ProductTrackingInfo[]) {
    this.customerAlsoBoughtSubject?.next(productTrackingInfos);
  }

  nextMayAlsoNeed(productTrackingInfos: ProductTrackingInfo[]) {
    this.mayAlsoNeedSubject?.next(productTrackingInfos);
  }

  nextRatingStarts(ratingStarts: number) {
    if (ratingStarts > 0) {
      this.ratingStartsSubject?.next(ratingStarts);
    }
  }

  nextQuestion(questionCount: number) {
    if (questionCount > 0) {
      this.questionCountSubject?.next(questionCount);
    }
  }

  trackFeatureLoad(
    productNumber: number,
    productAttributeList?: string,
    betterBuyProductNumber?: number,
    betterBuyProductAttributeList?: string,
  ) {
    const timeOut = 5000;

    this.recommendations$?.unsubscribe();
    this.recommendations$ = combineLatest([
      this.customerAlsoBought$,
      this.mayAlsoNeed$.pipe(filter(products => products !== undefined)),
      this.ratingStarts$.pipe(takeUntil(interval(timeOut)), defaultIfEmpty(0)),
      this.questionCount$.pipe(takeUntil(interval(timeOut)), defaultIfEmpty(0)),
    ])
      .pipe(take(1))
      .subscribe(
        ([customerAlsoBought, mayAlsoNeed, ratingStarts, questionCount]) => {
          if (
            customerAlsoBought?.length > 0 ||
            mayAlsoNeed?.length > 0 ||
            ratingStarts > 0 ||
            questionCount > 0
          ) {
            this.productDetailService.trackFeatureLoad(
              productNumber,
              productAttributeList,
              customerAlsoBought,
              mayAlsoNeed,
              ratingStarts,
              questionCount,
              betterBuyProductNumber,
              betterBuyProductAttributeList,
            );
          }
        },
      );
  }

  reset() {
    this.recommendations$?.unsubscribe();
    this.mayAlsoNeedSubject.next(undefined);
    this.ratingStartsSubject.next(0);
  }

  getRatingStarts() {
    let averageRating = 0;
    const timeOut = 5000;
    this.ratingStarts$
      .pipe(takeUntil(interval(timeOut)), defaultIfEmpty(0))
      .pipe(take(1))
      .subscribe(rating => {
        averageRating = rating;
      });

    return averageRating;
  }
}
